import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/api/client";
import type {
  GenerateReportRequest,
  GenerateReportResponse,
  GeneratedReportDetail,
  GeneratedReportListPage,
} from "@/api/types";

const LIST_REFETCH_MS = 30_000;
// Detail polling: cada 3s para captar la transición pending → ready
// (la generación toma ~10-30s con weasyprint + IA).
const DETAIL_POLL_MS = 3_000;

interface ReportsListArgs {
  page: number;
  perPage: number;
  reportType?: string;
  year?: number;
}

export function useReports({
  page,
  perPage,
  reportType,
  year,
}: ReportsListArgs) {
  return useQuery({
    queryKey: ["reports", { page, perPage, reportType, year }],
    queryFn: ({ signal }) => {
      const params = new URLSearchParams();
      params.set("page", String(page));
      params.set("per_page", String(perPage));
      if (reportType) params.set("report_type", reportType);
      if (year !== undefined) params.set("year", String(year));
      return api<GeneratedReportListPage>(
        `/api/v1/reports?${params.toString()}`,
        { signal },
      );
    },
    refetchInterval: LIST_REFETCH_MS,
    placeholderData: (prev) => prev,
  });
}

/**
 * Hook de detalle que pollsea cada 3 s mientras `pdf_available=false`
 * para captar la transición pending → ready post-POST /generate.
 */
export function useReport(reportId: string | undefined, options?: { poll?: boolean }) {
  return useQuery({
    queryKey: ["report", reportId],
    queryFn: ({ signal }) =>
      api<GeneratedReportDetail>(`/api/v1/reports/${reportId}`, { signal }),
    enabled: !!reportId,
    refetchInterval: (query) => {
      if (!options?.poll) return false;
      const data = query.state.data as GeneratedReportDetail | undefined;
      if (data && data.pdf_available) return false;
      return DETAIL_POLL_MS;
    },
  });
}

export function useGenerateReport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: GenerateReportRequest) =>
      api<GenerateReportResponse>("/api/v1/reports/generate", {
        method: "POST",
        body,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["reports"] });
    },
  });
}
